import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HeaderService } from '@core/services/header.service';
import { AttendanceTimerService, StopwatchState } from '@core/services/attendance-timer.service';

@Component({
  selector: 'app-attendance-stopwatch',
  templateUrl: './attendance-stopwatch.component.html',
  styleUrls: ['./attendance-stopwatch.component.scss'],
  standalone: true,
  imports: [CommonModule, MatIconModule, MatProgressSpinnerModule]
})
export class AttendanceStopwatchComponent implements OnInit, OnDestroy {

  timerDisplay    = '00:00:00';
  state: StopwatchState = 'IDLE';
  locationWarning = false;
  loading         = false;

  get canCheckIn():  boolean { return !this.loading && (this.state === 'IDLE' || this.state === 'CHECKED_OUT'); }
  get canCheckOut(): boolean { return !this.loading && this.state === 'RUNNING'; }
  get isSyncing():   boolean { return this.state === 'SYNCING'; }

  currentUserId = '';
  private subs = new Subscription();

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private headerService: HeaderService,
    private snackbar: SnackbarService,
    public timerService: AttendanceTimerService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    this.currentUserId = user?.id || '';

    this.subs.add(this.timerService.display$.subscribe(d => this.timerDisplay = d));
    this.subs.add(this.timerService.session$.subscribe(s => {
      this.state = s.state;
    }));

    this.loadTodayAttendance();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }


  loadTodayAttendance(): void {
    const today     = this._dateStr(new Date());
    const yesterday = this._dateStr(new Date(Date.now() - 86_400_000));

    this.timerService.setSyncing();

    this.apiService.get(`/attendance?date=${today}`).subscribe({
      next: (todayRecords: any[]) => {
        const mine = (todayRecords || []).filter(
          (r: any) => r.employeeId === this.currentUserId &&
                      (r.date || '').split('T')[0] === today
        );
        const hasOpenToday = mine.some((r: any) => r.checkIn && !r.checkOut);

        if (hasOpenToday) {
          this.timerService.hydrateFromRecords(mine);
        } else {
          this.apiService.get(`/attendance?date=${yesterday}`).subscribe({
            next: (yRecords: any[]) => {
              const yMine = (yRecords || []).filter(
                (r: any) => r.employeeId === this.currentUserId &&
                            (r.date || '').split('T')[0] === yesterday &&
                            r.checkIn && !r.checkOut
              );
              this.timerService.hydrateFromRecords([...yMine, ...mine]);
            },
            error: () => this.timerService.hydrateFromRecords(mine)
          });
        }
      },
      error: () => {
        this.timerService.hydrateFromRecords([]);
      }
    });
  }


  async checkIn(): Promise<void> {
    const isMobile = this._isMobile();

    this.loading = true;
    this.locationWarning = false;

    const pressedAt = new Date().toISOString();

    const loc = await this._getLocationOrNull();

    if (!loc.latitude) {
      this.locationWarning = true;
      this.loading = false;
      this._logAttempt('check_in_failed', this.locationErrorReason || 'location unavailable', null, null);

      if (this.locationDenied) {
        this.showLocationHelp = true;
      } else {
        this.snackbar.showFailure(
          this.locationErrorReason || 'We need your location to check you in. Please allow location access and try again.');
      }
      return;
    }

    const payload = {
      employeeId:        this.currentUserId,
      latitude:          loc.latitude,
      longitude:         loc.longitude,
      accuracy:          loc.accuracy,
      checkInMode:       isMobile ? 'Mobile' : 'Web',
      notes:             'present',
      clientCheckInTime: pressedAt
    };

    this.apiService.post('/attendance/check-in', payload).subscribe({
      next: (record: any) => {
        this.timerService.startSession(
          record.id,
          new Date(pressedAt),
          this.timerService.currentSession.accumulatedSeconds
        );
        this.loading = false;
        this.loadTodayAttendance();
      },
      error: (err: any) => {
        const rawMsg: string = (err?.error?.message || err?.error?.error || '').trim();
        const msg: string = rawMsg.toLowerCase();
        this._logAttempt('check_in_failed', msg || 'unknown error', loc.latitude, loc.longitude);

        if (msg.includes('already') || err?.status === 409) {
          this.snackbar.showInfo('Already checked in — syncing latest state');
          this.loadTodayAttendance();
        } else if (!loc.latitude && this.locationErrorReason) {
          this.snackbar.showFailure(this.locationErrorReason);
        } else if (msg.includes('outside') || msg.includes('geofence') || msg.includes('zone') || msg.includes('radius') || msg.includes('location')) {
          this.snackbar.showFailure(rawMsg || 'You are outside the allowed check-in zone. Move to your designated work location and try again.');
        } else {
          this.snackbar.showFailure('Check-in failed. Please try again.');
        }

        this.loading = false;
      }
    });
  }


  async checkOut(): Promise<void> {
    if (!this.timerService.currentSession.attendanceId) {
      this.snackbar.showFailure('No active check-in found.');
      return;
    }

    // Accidental-checkout guard: elapsed under 30 minutes is almost always a mis-tap.
    const parts = this.timerDisplay.split(':').map(Number);
    const elapsedMins = parts.length === 3 ? parts[0] * 60 + parts[1] : 999;
    if (elapsedMins < 30) {
      const ok = await this.confirmDialog.confirm(
        `You checked in only ${elapsedMins <= 1 ? 'a minute' : elapsedMins + ' minutes'} ago — check out anyway?`,
        { title: 'Confirm Check Out', confirmLabel: 'Check Out', danger: true },
      );
      if (!ok) return;
    }

    this.loading = true;

    const loc = await this._getLocationOrNull();

    this.apiService.post('/attendance/check-out', {
      employeeId: this.currentUserId,
      latitude:   loc.latitude,
      longitude:  loc.longitude,
      notes:      'present'
    }).subscribe({
      next: () => {
        this.timerService.endSession();
        this.headerService.stopTimer();
        this.loading = false;
        this.loadTodayAttendance();
      },
      error: () => {
        this.snackbar.showFailure('Check-out failed. Please try again.');
        this.loading = false;
      }
    });
  }


  locationErrorReason = '';
  locationDenied = false;
  showLocationHelp = false;

  private _getLocationOrNull(): Promise<{ latitude: number | null; longitude: number | null; accuracy: number | null }> {
    this.locationErrorReason = '';
    this.locationDenied = false;
    return new Promise(resolve => {
      if (!navigator.geolocation) {
        this.locationErrorReason = 'This device/browser does not support location.';
        resolve({ latitude: null, longitude: null, accuracy: null });
        return;
      }
      if (!window.isSecureContext) {
        this.locationErrorReason = 'Location needs a secure (https) connection. Please open the site via its https address.';
        resolve({ latitude: null, longitude: null, accuracy: null });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        err => {
          this.locationDenied = err.code === err.PERMISSION_DENIED;
          this.locationErrorReason =
            err.code === err.PERMISSION_DENIED ? 'Location permission was blocked. Allow location for this site and try again.'
          : err.code === err.POSITION_UNAVAILABLE ? 'Your location could not be determined. Move near a window or enable GPS, then retry.'
          : err.code === err.TIMEOUT ? 'Getting your location took too long. Please try again.'
          : 'Could not read your location.';
          resolve({ latitude: null, longitude: null, accuracy: null });
        },
        { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 }
      );
    });
  }

  private _isMobile(): boolean {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  get locationHelpSteps(): string[] {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) {
      return [
        'Open the iPhone Settings app.',
        'Go to Privacy & Security → Location Services, and make sure it is on.',
        'Scroll to your browser (Safari / Chrome) and set it to “While Using”.',
        'Also check Settings → Safari (or Chrome) → Location is set to Allow.',
        'Return here and tap Check In again.',
      ];
    }
    if (/Android/i.test(ua)) {
      return [
        'Tap the lock / ⓘ icon to the left of the web address.',
        'Open “Permissions” (or “Site settings”).',
        'Set Location to Allow.',
        'Make sure your phone’s GPS/Location is turned on.',
        'Return here and tap Check In again.',
      ];
    }
    return [
      'Click the lock (or ⓘ / location) icon at the left of the address bar.',
      'Find “Location” and change it to Allow.',
      'Reload the page.',
      'Click Check In again.',
    ];
  }

  closeLocationHelp(): void { this.showLocationHelp = false; }

  retryFromHelp(): void {
    this.showLocationHelp = false;
    this.checkIn();
  }

  private _logAttempt(type: string, reason: string, lat?: number | null, lon?: number | null): void {
    this.apiService.post('/attendance/log-attempt', {
      employeeId: this.currentUserId,
      eventType:  type,
      deviceMode: this._isMobile() ? 'Mobile Browser' : 'Web',
      reason,
      latitude:   lat  ?? null,
      longitude:  lon  ?? null,
    }).subscribe({ error: () => {} });
  }

  private _dateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }
}
